import React from 'react';
import { Link } from 'react-router-dom';
import SeoLandingLayout from '../../components/SeoLandingLayout/SeoLandingLayout';
import {
  IA_BRAND_NAME,
  IA_CASOS,
  IA_HUB,
  IA_SERVICES,
  buildIaBreadcrumb,
  buildIaServiceSchema,
} from '../../config/iaBrand';

const SERVICE = IA_SERVICES.agentes;

const FAQ = [
  {
    question: '¿Hacéis agentes IA “baratos” para cualquier pyme?',
    answer:
      'No competimos por ser el agente más barato de Canarias. El valor está en integrar el agente con Business Central, Vulpin, CRM o los sistemas que ya os gestionamos, para que automatice de verdad y no quede como un chatbot suelto.',
  },
  {
    question: '¿Sirve si ya soy cliente de E-ASY?',
    answer:
      'Es el escenario ideal. Si ya mantenemos vuestro ERP, Vulpin o integraciones, el agente se apoya en datos y flujos reales. Por eso el CTA es hablar con tu gestor de cuenta.',
  },
  {
    question: '¿WhatsApp, email o CRM?',
    answer:
      'Según el proceso. Empezamos por el cuello de botella repetitivo (citas, incidencias, pedidos, seguimiento) y elegimos el canal donde más impacto hay con menos fricción.',
  },
  {
    question: '¿Y si el agente ya está hecho y solo hay que desplegarlo?',
    answer:
      'Eso es IA en producción: backend, API y Azure para que el prototipo aguante el día a día. Esta ficha es diseñar e integrar el agente con vuestro ERP o CRM. Se pueden encadenar.',
  },
];

const AgentesIAPage = ({ darkMode, toggleDarkMode }) => (
  <SeoLandingLayout
    darkMode={darkMode}
    toggleDarkMode={toggleDarkMode}
    title="Agentes IA para empresas | Integrados con tu ERP | E-ASY"
    description="Agentes IA integrados con Business Central, Vulpin o CRM para quitar tareas repetitivas. Servicio en remoto para toda España, con sede en Tenerife."
    keywords="agentes IA para empresas, automatización con IA, integrar IA con Business Central, integrar IA con ERP, agentes IA pyme"
    canonical={SERVICE.url}
    h1="Agentes IA integrados con los sistemas que ya usas"
    lede="Automatizamos procesos repetitivos con agentes que hablan con Business Central, Vulpin o tu CRM. No vendemos el chatbot más barato: conectamos IA a tu operación real."
    ctaTo="/contacto"
    ctaLabel="Habla con tu gestor de cuenta"
    footerCtaLabel="Habla con tu gestor de cuenta"
    faqItems={FAQ}
    serviceJsonLd={buildIaServiceSchema({
      name: 'Agentes IA y automatización para pymes',
      description:
        'Diagnóstico de procesos, implementación de agentes (WhatsApp, email, CRM) e integración con Business Central, Vulpin y sistemas gestionados por E-ASY.',
      url: SERVICE.url,
      serviceType: 'Automatización con agentes de inteligencia artificial',
    })}
    serviceJsonLdId="jsonld-service-agentes-ia"
    breadcrumbJsonLd={buildIaBreadcrumb([
      { name: IA_HUB.label, url: IA_HUB.url },
      { name: SERVICE.label, url: SERVICE.url },
    ])}
    cobertura
  >
    <h2>El problema</h2>
    <p>
      El riesgo para una pyme no es pagar de más: es montar un bot que no conoce pedidos, stock,
      expedientes o el CRM, y acaba generando más trabajo del que quita. En Canarias ese mercado ya está
      saturado de ofertas genéricas, y el mismo problema se repite en el resto de España.
    </p>

    <h2>La solución</h2>
    <p>
      En {IA_BRAND_NAME} diseñamos <strong>agentes IA para empresas</strong> apoyándonos en la integración
      con los sistemas que ya gestionamos: <strong>Business Central</strong>, <strong>Vulpin</strong>, CRM
      y el resto del stack del cliente. El agente no es un producto suelto; es una pieza de automatización
      dentro de tu operación.
    </p>

    <h2>Qué incluye</h2>
    <ul>
      <li>Diagnóstico de procesos repetitivos</li>
      <li>Implementación de agente (WhatsApp, email, CRM)</li>
      <li>Integración nativa con los sistemas que ya gestiona E-ASY para el cliente</li>
    </ul>

    <h2>Por qué con E-ASY</h2>
    <p>
      Si ya eres cliente, tu gestor conoce el entorno. Eso reduce fricción, tiempos y errores frente a un
      proveedor que solo “pone un agente” y se va. Si además necesitas soberanía del dato (menores,
      dependencia, administración), mira{' '}
      <Link to={IA_SERVICES.privada.path}>{IA_SERVICES.privada.label}</Link>
      — no es lo mismo un agente conectado a APIs externas que un modelo en tu infraestructura.
    </p>
    <p>
      También puedes revisar{' '}
      <Link to="/vulpin">Vulpin Suite</Link>
      {' '}si el cuello es la gestión,{' '}
      <Link to="/integracion-sistemas">integración de sistemas</Link>
      {' '}o, si ya habéis montado un prototipo por vuestra cuenta y no sabéis
      desplegarlo,{' '}
      <Link to="/servicios/ia-en-produccion">IA en producción</Link>.
    </p>

    <h2>Siguiente paso</h2>
    <p>
      Este servicio está pensado sobre todo para clientes existentes. Habla con tu gestor de cuenta y
      priorizamos el proceso con más retorno.
    </p>
    <p>
      <Link to="/contacto" className="seo-landing-cta">
        Habla con tu gestor de cuenta
      </Link>
    </p>
    <p>
      Lectura útil:{' '}
      <Link to={`/blog/${SERVICE.blogSlug}`}>
        Dónde desplegar un agente de IA: Azure, on-premise o API pública
      </Link>
      . Si el prototipo ya existe:{' '}
      <Link to="/blog/de-prototipo-chatgpt-cursor-a-produccion">
        de prototipo a producción
      </Link>
      . Referencias de implantación en entidades:{' '}
      <Link to={IA_CASOS.path}>{IA_CASOS.label}</Link>.
    </p>
  </SeoLandingLayout>
);

export default AgentesIAPage;
